import { translate } from '@util/Localization';
import { UserData } from 'hydra-data-types';
import React, { ReactElement } from 'react';
import { ScreenConfigBase, ScreenType } from '../../config/FlowTypes';
import { AppSelector, useAppStore } from '../../store/Store';
import { ScreenWrapper } from '../styles/Containers';
import { NavigationProps } from './FlowNavigation';
import { PresentationLayout } from './layouts/PresentationLayout';

export type HypnotisabilityResultsScreenConfig<ScreenNames extends string> = ScreenConfigBase<
  ScreenType.HypnotisabilityResults,
  ScreenNames
>;

type Props<ScreenNames extends string> = NavigationProps<
  HypnotisabilityResultsScreenConfig<ScreenNames>,
  ScreenNames
>;

export const HypnotisabilityResultsScreen = <ScreenNames extends string>(
  props: Props<ScreenNames>
): ReactElement => {
  const { navigation } = props;
  const userData = useAppStore(AppSelector.userData) as Required<UserData>;

  const heading = calculateIsHighlyLikely(userData)
    ? translate('hypnotizability.heading.highlyLikely')
    : translate('hypnotizability.heading.likely');

  const body = calculateIsHighlyLikely(userData)
    ? Object.values(translate('hypnotizability.body.highlyLikely'))
    : Object.values(translate('hypnotizability.body.likely'));

  return (
    <ScreenWrapper>
      <PresentationLayout
        title={'The results are in'}
        content={[
          {
            type: 'coloredBox',
            heading: heading,
            body: [],
            textAlign: 'center',
          },
          ...body.map((content) => ({ type: 'text' as const, content })),
        ]}
        onPressPrimaryButton={() => navigation.next(props)}
        primaryButtonTitle={'Continue'}
      />
    </ScreenWrapper>
  );
};

function calculateIsHighlyLikely(userData: Required<UserData>) {
  // Combination of all quiz question answers that are reduced to a total value
  const quizScore =
    typeof userData.hypnotisabilityVividRecall !== 'undefined'
      ? [
          ...userData.hypnotisabilityVividRecall,
          userData.hypnotisabilityFocus,
          userData.hypnotisabilityAbsorption,
          userData.hypnotisabilityIntuition,
          userData.hypnotisabilityColorVisualization,
        ].reduce((a, b) => Number(a) + Number(b), 0)
      : 0;

  return quizScore >= 13;
}
